import { Link } from "react-router-dom";
import Logo from "../components/Logo";

export default function TermsOfService() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#faf8f3]/80 border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between h-16">
          <Logo />
          <div className="flex items-center gap-6">
            <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]">Browse</Link>
            <Link to="/contact" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]">Contact</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-5 md:px-12 py-16">
        <p className="text-xs font-semibold text-[#1a6b3c] uppercase tracking-wider mb-3">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2" data-testid="tos-heading">Terms of Service</h1>
        <p className="text-sm text-[#888] mb-10">Last updated: April 23, 2026</p>

        <div className="prose-container space-y-8 text-[#555] text-[15px] leading-relaxed">
          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">1. Agreement to Terms</h2>
            <p>By accessing or using the Low Key Rents platform ("Service"), operated by Low Key Rents ("Company," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.</p>
            <p className="mt-2">The Service is available at <a href="https://lowkey.rent" className="text-[#1a6b3c] hover:underline">lowkey.rent</a> and related subdomains. By creating an account, listing a property, submitting an application, or making a payment through our platform, you acknowledge that you have read, understood, and agree to these Terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">2. Eligibility</h2>
            <p>You must be at least 18 years of age and legally capable of entering into binding contracts to use the Service. By using the Service, you represent and warrant that you meet these requirements.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">3. User Accounts</h2>
            <p>To access certain features of the Service, you must register for an account. You agree to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
            </ul>
            <p className="mt-2">We reserve the right to suspend or terminate accounts that violate these Terms or for any other reason at our sole discretion.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">4. User Roles & Responsibilities</h2>
            <h3 className="font-semibold text-[#0d0d0d] mt-3 mb-1">Renters</h3>
            <p>As a renter, you agree to provide truthful information in your profile, video introduction, and applications. You understand that background checks are processed by third-party services, and results are shared with landlords you apply to. You agree not to share false, misleading, or fraudulent information.</p>
            <h3 className="font-semibold text-[#0d0d0d] mt-3 mb-1">Landlords / Property Owners</h3>
            <p>As a landlord, you represent that you have the legal right to list the property and enter into rental agreements for listed properties. You agree to provide accurate listing information including pricing, location, amenities, availability, and property condition. You are responsible for compliance with all applicable federal, state, and local housing laws, including but not limited to the Fair Housing Act.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">5. Listings & Content</h2>
            <p>You retain ownership of content you post on the Service. By posting content (including photos, videos, descriptions, and reviews), you grant Low Key Rents a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and distribute such content in connection with operating and promoting the Service.</p>
            <p className="mt-2">You agree not to post content that is:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>False, misleading, or fraudulent</li>
              <li>Infringing on the intellectual property rights of others</li>
              <li>Discriminatory based on race, color, religion, sex, national origin, disability, or familial status</li>
              <li>Illegal, harmful, threatening, abusive, or otherwise objectionable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">6. Background Checks</h2>
            <p>Low Key Rents offers background check services through third-party providers. Background checks may include criminal history, eviction history, credit summary, and identity verification. By initiating a background check, you consent to these searches and understand that:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Background check fees ($35 plus processing fees) are non-refundable</li>
              <li>Results are provided "as-is" from third-party sources</li>
              <li>Low Key Rents does not guarantee the accuracy or completeness of background check results</li>
              <li>Landlords are responsible for compliance with the Fair Credit Reporting Act (FCRA) when using background check information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">7. Payments & Fees</h2>
            <p>Low Key Rents facilitates payments between renters and landlords through Stripe. By using payment features, you agree to Stripe's terms of service in addition to these Terms.</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>A platform processing fee of 3% is applied to all transactions</li>
              <li>Background check payments are non-refundable once processed</li>
              <li>Rent payments are processed securely and disbursed to landlords per our payment schedule</li>
              <li>Landlords must connect a valid Stripe account to receive payouts</li>
              <li>Low Key Rents is not responsible for payment disputes between renters and landlords beyond facilitating communication</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">8. Messaging & Communication</h2>
            <p>All communication between renters and landlords should occur through the Low Key Rents in-app messaging system. Sharing personal contact information (phone numbers, email addresses, social media) outside the platform is discouraged for safety purposes. We reserve the right to monitor messages for violations of these Terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">9. Digital Lease Agreements</h2>
            <p>Low Key Rents may facilitate digital lease signing. Digital signatures executed through the platform are intended to be legally binding. However, Low Key Rents is not a party to any lease agreement and does not provide legal advice. We recommend consulting with a qualified attorney before signing any lease agreement.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">10. Prohibited Activities</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
              <li>Post fraudulent listings or misrepresent property conditions</li>
              <li>Discriminate against any protected class in rental decisions</li>
              <li>Harass, threaten, or intimidate other users</li>
              <li>Attempt to circumvent the platform's payment system</li>
              <li>Use automated systems (bots, scrapers) to access the Service</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Create multiple accounts for deceptive purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">11. Limitation of Liability</h2>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, LOW KEY RENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE. Low Key Rents acts as a platform connecting renters and landlords and is not a party to any rental agreement. We do not guarantee the quality, safety, or legality of any listed property, the truth or accuracy of any listing or user content, or the ability of landlords or renters to complete transactions.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">12. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless Low Key Rents, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of or relating to your use of the Service, your violation of these Terms, or your violation of any rights of a third party.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">13. Modifications to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on the Service and updating the "Last updated" date. Your continued use of the Service after any modifications constitutes acceptance of the updated Terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">14. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Virginia, without regard to its conflict of law provisions. The Service operates in Virginia, Maryland, and the District of Columbia. Any disputes arising under these Terms shall be resolved in the state or federal courts located in Virginia.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">15. Contact Information</h2>
            <p>If you have questions about these Terms, please contact us:</p>
            <div className="mt-2 bg-white rounded-xl border border-black/[0.06] p-4 space-y-1">
              <p><strong>Low Key Rents</strong></p>
              <p>General: <a href="mailto:hello@lowkey.rent" className="text-[#1a6b3c] hover:underline">hello@lowkey.rent</a></p>
              <p>Support: <a href="mailto:support@lowkey.rent" className="text-[#1a6b3c] hover:underline">support@lowkey.rent</a></p>
            </div>
          </section>
        </div>
      </div>

      <footer className="border-t border-black/[0.06] px-5 md:px-12 py-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#888]">&copy; 2026 Low Key Rents. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs text-[#888] hover:text-[#1a6b3c]">Privacy Policy</Link>
            <Link to="/contact" className="text-xs text-[#888] hover:text-[#1a6b3c]">Contact</Link>
            <a href="mailto:support@lowkey.rent" className="text-xs text-[#888] hover:text-[#1a6b3c]">support@lowkey.rent</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
